import type { Deliberation } from '@/types/deliberation'

export interface DeliberationSemestreSummary {
	semestreId: number
	semestreNumero: number
	premiere: Deliberation | null
	seconde: Deliberation | null
}

export interface DeliberationSpecialiteSummary {
	specialiteId: number
	specialiteLabel: string
	specialiteShort: string
	semestres: DeliberationSemestreSummary[]
}

const ETAT_SHORT: Record<string, string> = {
	en_cours: '…',
	phase_1_terminee: 'P1',
	phase_2_terminee: 'P2',
	valide: '✓',
	annule: '✗',
}

const ETAT_COLOR: Record<string, string> = {
	en_cours: 'info',
	phase_1_terminee: 'primary',
	phase_2_terminee: 'warning',
	valide: 'success',
	annule: 'error',
}

export function getDeliberationEtatShort(etat?: string | null): string {
	if (!etat) return '—'
	return ETAT_SHORT[etat] || etat.slice(0, 3)
}

export function getDeliberationEtatColor(etat?: string | null): string {
	if (!etat) return 'grey'
	return ETAT_COLOR[etat] || 'grey'
}

function shortenLabel(label: string, max = 14): string {
	const trimmed = label.trim()
	if (trimmed.length <= max) return trimmed
	return `${trimmed.slice(0, max - 1)}…`
}

/** Regroupe les délibérations session → spécialité → semestre (même structure que DeliberationSessionContent). */
export function buildDeliberationSessionSummary(
	deliberations: Deliberation[]
): DeliberationSpecialiteSummary[] {
	const grouped: Record<number, { specialite: any; deliberations: Deliberation[] }> = {}

	for (const delib of deliberations) {
		const specId = delib.specialite?.id
		if (!specId) continue

		if (!grouped[specId]) {
			grouped[specId] = { specialite: delib.specialite, deliberations: [] }
		}
		grouped[specId].deliberations.push(delib)
	}

	return Object.values(grouped)
		.map((group) => {
			const label = group.specialite?.intitule || group.specialite?.code || 'Spécialité'
			const bySemestre = group.deliberations.reduce((acc: DeliberationSemestreSummary[], delib) => {
				const semId = delib.semestre?.id
				if (!semId) return acc

				let semGroup = acc.find((s) => s.semestreId === semId)
				if (!semGroup) {
					semGroup = {
						semestreId: semId,
						semestreNumero: delib.semestre?.numero ?? 0,
						premiere: null,
						seconde: null,
					}
					acc.push(semGroup)
				}

				if (delib.type_deliberation === 'premiere') {
					semGroup.premiere = delib
				} else if (delib.type_deliberation === 'seconde') {
					semGroup.seconde = delib
				}

				return acc
			}, [])

			bySemestre.sort((a, b) => a.semestreNumero - b.semestreNumero)

			return {
				specialiteId: group.specialite.id,
				specialiteLabel: label,
				specialiteShort: shortenLabel(group.specialite?.code || label),
				semestres: bySemestre,
			}
		})
		.sort((a, b) => a.specialiteLabel.localeCompare(b.specialiteLabel, 'fr'))
}

/** Couleur du badge semestre : état le moins avancé parmi 1ère / 2nde délibération. */
export function getSemestreSummaryColor(sem: DeliberationSemestreSummary): string {
	if (sem.premiere?.etat === 'annule' || sem.seconde?.etat === 'annule') return 'error'
	if (sem.seconde?.etat && sem.seconde.etat !== 'valide') {
		return getDeliberationEtatColor(sem.seconde.etat)
	}
	if (sem.premiere?.etat && sem.premiere.etat !== 'valide') {
		return getDeliberationEtatColor(sem.premiere.etat)
	}
	if (sem.premiere?.etat === 'valide') return 'success'
	return 'grey'
}

export function formatSemestreSummaryLine(sem: DeliberationSemestreSummary): string {
	const premiere = sem.premiere
		? `1ère ${getDeliberationEtatShort(sem.premiere.etat)}`
		: '1ère —'
	const seconde = sem.seconde
		? `2nde ${getDeliberationEtatShort(sem.seconde.etat)}`
		: '2nde —'
	return `S${sem.semestreNumero} : ${premiere} · ${seconde}`
}

export function formatSpecialiteSummaryTooltip(spec: DeliberationSpecialiteSummary): string {
	if (spec.semestres.length === 0) return spec.specialiteLabel
	return [
		spec.specialiteLabel,
		...spec.semestres.map((sem) => formatSemestreSummaryLine(sem)),
	].join('\n')
}

/** Ligne compacte affichée dans le chip : « S1 ✓✓ · S2 …— » */
export function formatSemestreCompact(sem: DeliberationSemestreSummary): string {
	const p = sem.premiere ? getDeliberationEtatShort(sem.premiere.etat) : '—'
	const s = sem.seconde ? getDeliberationEtatShort(sem.seconde.etat) : '—'
	return `S${sem.semestreNumero} ${p}${s}`
}
